import { Suspense, useEffect, useState } from "react";
import { NavLink, Outlet, ScrollRestoration, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { applyTheme, getTheme, type Theme } from "../lib/theme";
import { getPrefs, setPrefs } from "../lib/prefs";
import { loadAllSaved, REPORT_KEYS, type SavedView } from "../lib/viewState";
import { Icon } from "../lib/icons";
import { Toaster } from "../lib/toast";
import { DataStatus } from "../components/DataStatus";
import { CommandPalette, openPalette } from "../components/CommandPalette";
import { Tour } from "../components/Tour";
import { Skeleton, cn } from "../components/ui";
import { trackRoute } from "../lib/nav";

// One-thumb quick access on phones — the full menu is still one tap away via the header's ☰.
const MOBILE_TABS: Array<{ to: string; label: string; icon: string; end?: boolean }> = [
  { to: "/", label: "Обзор", icon: "overview", end: true },
  { to: "/report/current", label: "Остаток", icon: "box" },
  { to: "/report/daily", label: "По дням", icon: "calendarDay" },
  { to: "/alerts", label: "Проблемы", icon: "alert" },
  { to: "/settings", label: "Настройки", icon: "settings" },
];

// Same items/routes as before; the old plain separators became small ERP-style section labels.
const NAV: Array<{ to: string; label: string; icon: string; end?: boolean } | { sep: string }> = [
  { to: "/", label: "Обзор", icon: "overview", end: true },
  { sep: "Отчёты" },
  { to: "/report/current", label: "Текущий остаток", icon: "box" },
  { to: "/report/daily", label: "По дням", icon: "calendarDay" },
  { to: "/report/weekly", label: "По неделям", icon: "calendar" },
  { to: "/report/monthly", label: "По месяцам", icon: "calendarRange" },
  { to: "/report/suppliers", label: "По поставщикам", icon: "truck" },
  { to: "/report/brands", label: "По брендам", icon: "tag" },
  { to: "/all-reports", label: "Все отчёты", icon: "reports" },
  { sep: "Контроль" },
  { to: "/reconciliation", label: "Расхождения", icon: "scale" },
  { to: "/alerts", label: "Проблемы", icon: "alert" },
  { sep: "Система" },
  { to: "/settings", label: "Настройки", icon: "settings" },
];

const THEME_LABEL: Record<Theme, string> = { light: "светлая", dark: "тёмная", system: "как в системе" };

const REPORT_LABEL: Record<string, string> = {
  current: "Текущий остаток",
  daily: "По дням",
  weekly: "По неделям",
  monthly: "По месяцам",
  suppliers: "По поставщикам",
  brands: "По брендам",
};

export function AppShell() {
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(() => getPrefs().sideCollapsed);
  const [drawer, setDrawer] = useState(false);
  const [pins, setPins] = useState<Array<SavedView & { rk: string }>>([]);
  // remember where the user is, for the product card's "← Назад"
  useEffect(() => trackRoute(location.pathname, location.search), [location.pathname, location.search]);
  useEffect(() => {
    if (getPrefs().sideCollapsed !== collapsed) setPrefs({ sideCollapsed: collapsed });
  }, [collapsed]);
  useEffect(() => {
    let alive = true;
    loadAllSaved().then((all) => {
      if (!alive) return;
      const flat: Array<SavedView & { rk: string }> = [];
      for (const k of REPORT_KEYS) for (const v of all[k]) if (v.pin) flat.push({ ...v, rk: k });
      setPins(flat);
    });
    return () => {
      alive = false;
    };
  }, []);

  return (
    <div className={cn("grid min-h-screen grid-cols-1", collapsed ? "md:grid-cols-[60px_1fr]" : "md:grid-cols-[240px_1fr]", "print:block")}>
      {/* Sidebar uses its own sb-* tokens (index.css); since the 2026-09-28 minimalist pass they
          follow the light/dark theme instead of being fixed dark graphite. */}
      <aside
        className={cn(
          "z-50 flex h-screen flex-col border-r border-sidebar-border bg-sidebar transition-transform md:sticky md:top-0 md:translate-x-0 print:hidden",
          "fixed",
          collapsed ? "md:w-[60px]" : "w-[240px]",
          drawer ? "translate-x-0" : "-translate-x-full",
        )}
      >
        <div className={cn("flex h-14 flex-none items-center gap-2.5 border-b border-sidebar-border text-base font-semibold tracking-tight text-sidebar-text", collapsed ? "justify-center px-0" : "px-4")}>
          <span className="grid h-8 w-8 flex-none place-items-center rounded-lg bg-accent text-xs font-bold tracking-tight text-white">СА</span>
          {!collapsed && <span className="truncate">Складская аналитика</span>}
        </div>
        <nav className="flex flex-1 flex-col gap-0.5 overflow-y-auto overflow-x-hidden px-3 py-2">
          {NAV.map((n, i) =>
            "sep" in n ? (
              collapsed ? (
                <div key={i} className="mx-2 my-2 h-px bg-sidebar-border" />
              ) : (
                <div key={i} className="px-3 pb-1.5 pt-4 text-2xs font-semibold uppercase tracking-wider text-sidebar-text-2/70">
                  {n.sep}
                </div>
              )
            ) : (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                onClick={() => setDrawer(false)}
                title={collapsed ? n.label : undefined}
                className={({ isActive }) =>
                  cn(
                    "relative flex min-h-9 items-center gap-3 rounded-lg text-base transition-colors",
                    collapsed ? "justify-center px-0" : "px-3",
                    isActive
                      ? "bg-sidebar-accent-weak font-medium text-sidebar-accent"
                      : "text-sidebar-text-2 hover:bg-sidebar-2 hover:text-sidebar-text",
                  )
                }
              >
                <Icon name={n.icon} size={16} className="flex-none" />
                {!collapsed && <span className="truncate">{n.label}</span>}
              </NavLink>
            ),
          )}
          {pins.length > 0 && !collapsed && (
            <>
              <div className="px-3 pb-1.5 pt-4 text-2xs font-semibold uppercase tracking-wider text-sidebar-text-2/70">Мои виды</div>
              {pins.map((p) => (
                <NavLink
                  key={`${p.rk}:${p.id}`}
                  to={`/report/${p.rk}?view=${p.id}`}
                  onClick={() => setDrawer(false)}
                  title={REPORT_LABEL[p.rk]}
                  className={({ isActive }) =>
                    cn(
                      "flex min-h-9 items-center gap-3 rounded-lg px-3 text-base",
                      isActive
                        ? "bg-sidebar-accent-weak font-medium text-sidebar-accent"
                        : "text-sidebar-text-2 hover:bg-sidebar-2 hover:text-sidebar-text",
                    )
                  }
                >
                  <Icon name="star" size={16} className="flex-none text-warn" />
                  <span className="truncate">{p.name}</span>
                </NavLink>
              ))}
            </>
          )}
        </nav>
        <button
          type="button"
          onClick={() => setCollapsed((v) => !v)}
          title={collapsed ? "Развернуть меню" : "Свернуть меню"}
          className={cn(
            "mx-3 mb-3 mt-2 flex min-h-9 items-center gap-2 rounded-lg border-t-0 text-sm text-sidebar-text-2 hover:bg-sidebar-2 hover:text-sidebar-text",
            collapsed ? "justify-center px-0" : "px-3",
          )}
        >
          <Icon name="chevron" size={16} className={cn("flex-none transition-transform", !collapsed && "rotate-180")} />
          {!collapsed && <span>Свернуть</span>}
        </button>
      </aside>

      {drawer && <div className="fixed inset-0 z-40 bg-black/30 backdrop-blur-[1px] md:hidden" onClick={() => setDrawer(false)} />}

      <div className="min-w-0">
        <Header onMenu={() => setDrawer(true)} />
        <main className="mx-auto max-w-[1560px] px-4 pb-24 pt-6 md:px-8 md:pb-16 md:pt-8 print:max-w-none print:p-0">
          <div key={location.pathname} className="animate-fade-in">
            {/* pages are code-split (App.tsx) — show a quiet placeholder while a chunk loads */}
            <Suspense fallback={<Skeleton className="h-[320px] rounded-xl" />}>
              <Outlet />
            </Suspense>
          </div>
        </main>
      </div>

      {/* Phone-width bottom tab bar — the sidebar becomes an off-canvas drawer below md, so the
          5 most-used destinations get one-thumb access without opening it (2026-09-12 UI pass). */}
      <nav className="fixed inset-x-0 bottom-0 z-50 grid grid-cols-5 print:hidden border-t border-sidebar-border bg-sidebar/90 pb-[env(safe-area-inset-bottom)] backdrop-blur-md md:hidden">
        {MOBILE_TABS.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            className={({ isActive }) =>
              cn(
                "flex flex-col items-center gap-0.5 py-2 text-2xs",
                isActive ? "font-medium text-accent" : "text-sidebar-text-2",
              )
            }
          >
            <Icon name={n.icon} size={20} />
            <span className="truncate">{n.label}</span>
          </NavLink>
        ))}
      </nav>

      <Toaster />
      {/* window scroll position is restored on browser back / forward */}
      <ScrollRestoration />
      <CommandPalette />
      <Tour />
    </div>
  );
}

function Header({ onMenu }: { onMenu: () => void }) {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [theme, setTheme] = useState<Theme>(getTheme());

  const cycle = () => {
    const next: Theme = theme === "light" ? "dark" : theme === "dark" ? "system" : "light";
    setTheme(next);
    applyTheme(next);
  };

  return (
    <header className="sticky top-0 z-20 flex h-14 items-center gap-2 border-b print:hidden border-border bg-surface/95 px-4 backdrop-blur md:px-8">
      <button type="button" onClick={onMenu} title="Меню" className="grid h-9 w-9 place-items-center rounded-lg text-text-2 hover:bg-surface-2 md:hidden">
        <Icon name="menu" size={18} />
      </button>
      {/* Looks like a search field; opens the Ctrl+K palette (live product search + pages + views).
          Enter on the first palette row keeps the old "search in the report" behaviour. */}
      <button
        type="button"
        onClick={openPalette}
        title="Поиск по товарам, отчётам и видам [Ctrl K]"
        className="flex min-h-9 min-w-0 max-w-[480px] flex-1 items-center gap-2 rounded-lg border border-border bg-surface-3 px-2.5 text-left text-sm text-muted hover:border-border-strong hover:bg-surface"
      >
        <Icon name="search" size={16} className="flex-none" />
        <span className="min-w-0 flex-1 truncate">
          <span className="sm:hidden">Поиск…</span>
          <span className="hidden sm:inline">Поиск товара, SKU, категории или бренда…</span>
        </span>
        <kbd className="hidden flex-none rounded border border-border bg-surface px-1.5 text-2xs text-text-2 sm:block">Ctrl K</kbd>
      </button>
      <div className="flex-1" />
      <DataStatus />
      <button
        type="button"
        onClick={cycle}
        title={`Тема: ${THEME_LABEL[theme]} — нажмите, чтобы сменить`}
        aria-label="Сменить тему"
        className="grid h-9 w-9 flex-none place-items-center rounded-lg text-text-2 hover:bg-surface-2 hover:text-text"
      >
        <Icon name={theme === "dark" ? "moon" : theme === "light" ? "sun" : "monitor"} size={16} />
      </button>
      {user && (
        <button
          type="button"
          onClick={() => logout().then(() => navigate("/login"))}
          className="hidden min-h-9 items-center gap-2 rounded-lg pl-1.5 pr-2.5 text-sm text-text-2 hover:bg-surface-2 hover:text-text sm:inline-flex"
          title={`${user.name || user.username} · ${user.role === "admin" ? "Администратор" : "Менеджер"}\nНажмите, чтобы выйти`}
        >
          <span className="grid h-6 w-6 place-items-center rounded-full bg-surface-2 text-2xs font-semibold uppercase text-text ring-1 ring-border">
            {(user.name || user.username).slice(0, 1)}
          </span>
          {user.username}
          <Icon name="logout" size={16} className="text-muted" />
        </button>
      )}
    </header>
  );
}
